import {
  AlignLeft,
  AtSign,
  Calendar,
  CheckSquare,
  ChevronDown,
  FileUp,
  Hash,
  Heading2,
  Plus,
  Type,
  User2,
} from "lucide-react";
import { FORM_FIELD_KINDS, type FormFieldKind } from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Dropdown launcher for inserting a new field. Two variants — `default` for
// the "Add field" pill at the end of a section, `inline` for the
// hover-revealed floating "+" between two field rows + the small button in
// the FieldConfigPanel header.

const LAYOUT_KINDS: FormFieldKind[] = FORM_FIELD_KINDS.filter(
  (k): k is "section_heading" => k === "section_heading",
);
const INPUT_KINDS: FormFieldKind[] = FORM_FIELD_KINDS.filter(
  (k) => k !== "section_heading",
);

const LABELS: Record<FormFieldKind, string> = {
  text: "Short text",
  long_text: "Long text",
  email: "Email",
  number: "Number",
  date: "Date",
  partner_lookup: "Partner lookup",
  section_heading: "Section heading",
  file: "File upload",
  select: "Select",
  checkbox: "Checkbox",
};

export function AddFieldButton({
  onAdd,
  variant = "default",
  ariaLabel,
}: {
  onAdd: (kind: FormFieldKind) => void;
  variant?: "default" | "inline";
  ariaLabel?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "inline" ? (
          <Button
            type="button"
            size="icon"
            variant="outline"
            aria-label={ariaLabel ?? "Add field"}
            className="h-5 w-5 rounded-full border-accent bg-background shadow-sm hover:bg-accent hover:text-accent-foreground"
          >
            <Plus className="h-3 w-3" />
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label={ariaLabel ?? "Add field"}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add field
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Inputs
        </DropdownMenuLabel>
        {INPUT_KINDS.map((kind) => (
          <DropdownMenuItem key={kind} onClick={() => onAdd(kind)}>
            <FieldKindIcon kind={kind} />
            <span className="ml-2">{LABELS[kind]}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Layout
        </DropdownMenuLabel>
        {LAYOUT_KINDS.map((kind) => (
          <DropdownMenuItem key={kind} onClick={() => onAdd(kind)}>
            <FieldKindIcon kind={kind} />
            <span className="ml-2">{LABELS[kind]}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const FIELD_KIND_LABELS = LABELS;

function FieldKindIcon({ kind }: { kind: FormFieldKind }) {
  const Icon =
    kind === "text"
      ? Type
      : kind === "long_text"
        ? AlignLeft
        : kind === "email"
          ? AtSign
          : kind === "number"
            ? Hash
            : kind === "date"
              ? Calendar
              : kind === "partner_lookup"
                ? User2
                : kind === "section_heading"
                  ? Heading2
                  : kind === "file"
                    ? FileUp
                    : kind === "select"
                      ? ChevronDown
                      : CheckSquare;
  return <Icon className="h-3.5 w-3.5 text-muted-foreground" />;
}
